/**
 * HexAlertPopover — hover card for a hex. Shows the camera's full breakdown that the hex
 * itself only hints at: the latest alert (snapshot, name, description, time) and the
 * camera's LIVE / RECORDING status with its per-severity alert counts. Screen-space,
 * non-interactive.
 *
 * READING ORDER: the ALERT first, the camera second.
 * Somebody hovering a lit hexagon is asking "what happened?", not "is this camera
 * recording?" — the status chips and the counts are what they check after they know. The
 * alert used to sit at the bottom, under the counts, so the answer to the only question
 * being asked was the last thing on the card and the snapshot above it went unexplained.
 * Now the name is laid OVER the snapshot it describes (a caption belongs on its picture,
 * and on a 248px card an overlay costs no vertical space at all), the description sits
 * directly under it, and the camera's own state follows behind a rule.
 *
 * THE SNAPSHOT IS NOT ALWAYS A URL. On a local node `alert.snapshotUrl` is directly
 * loadable; on the CLOUD it is null — there is no route to a node's image store, so the
 * bytes have to be blob-fetched through the tunnel by `loadPreview`. Reading only
 * `snapshotUrl` is why this card showed no picture at all on a cloud board. It now resolves
 * the same two-tier way AlertCard does, through hexPreviewCache so that a pointer sweeping
 * the board costs one request per alert rather than one per hover, and it RESERVES the media
 * box while resolving — a card that grew by an image's height under a stationary pointer
 * would move its own text out from under the eye reading it.
 *
 * Styled as a diagram CALLOUT (see mock ss4.png): a thin leader line runs from a dot on
 * the hex's edge, up a 45° diagonal to an elbow, then along a horizontal rule that the
 * card sits on — all tinted with the alert's severity colour (neutral grey when the
 * camera has no active alert). Prefers up-and-right of the hex; mirrors left / flips
 * below when it would leave the viewport.
 */
import { useEffect, useState } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import type { HexAlert, HexCounter } from '../../api/contracts';
import { cachedPreview, resolvePreview } from './hexPreviewCache';

const CARD_W = 248;
const MARGIN = 8;
/** Length of the 45° diagonal leader segment (run == rise). */
const DIAG = 26;

/** Brand severity colours — leader line, rule and title tint. */
const SEV_COLOR: Record<string, string> = {
  critical: '#e61b24',
  warning: '#f59e0b',
  normal: '#015ab5',
};
/** Callout tint when the camera has no active alert. */
const NEUTRAL_COLOR = '#64748b';

const SEV_CHIP: Record<string, string> = {
  critical: 'bg-red-500 text-white',
  warning: 'bg-amber-400 text-amber-950',
  normal: 'bg-blue-500 text-white',
};

/**
 * The alert name's colour, in TWO ramps — the name is drawn on two very different surfaces
 * and one set of values cannot serve both.
 *
 * Over the snapshot it sits on a near-black scrim, so it needs the LIGHT end of each hue
 * (the same shades AlertTileStack uses over its own overlays); the mid tones that read as
 * "red" on paper go muddy there, and `--brand`-style blues disappear outright. On the card
 * itself it sits on `bg-popover`, so it needs the ramp the rest of the app uses for severity
 * text, dark-mode variant included.
 */
const SEV_NAME_ON_IMAGE: Record<string, string> = {
  critical: 'text-red-400',
  warning: 'text-amber-300',
  normal: 'text-sky-300',
};
const SEV_NAME: Record<string, string> = {
  critical: 'text-red-600 dark:text-red-400',
  warning: 'text-amber-600 dark:text-amber-400',
  normal: 'text-blue-600 dark:text-blue-400',
};

const SEV_COUNTS: { key: keyof HexCounter; label: string; dot: string }[] = [
  { key: 'critical', label: 'Critical', dot: 'bg-red-500' },
  { key: 'warning', label: 'Warning', dot: 'bg-amber-400' },
  { key: 'normal', label: 'Info', dot: 'bg-blue-500' },
];

function fmtTime(ts?: number): string {
  if (!ts) return '';
  const ms = ts < 1e12 ? ts * 1000 : ts;
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/** Compact inline status: dot + label, green when running, grey when off. */
function StatusChip({ label, on }: { label: string; on: boolean }) {
  return (
    <span className={`flex items-center gap-1 text-[10px] font-medium ${on ? 'text-emerald-500' : 'text-muted-foreground'}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${on ? 'bg-emerald-400' : 'bg-slate-400/60'}`} />
      {label}
    </span>
  );
}

interface HexAlertPopoverProps {
  /** Latest active alert for this camera, if any. */
  alert?: HexAlert;
  name: string;
  live: boolean;
  recording: boolean;
  counter?: HexCounter;
  anchorX: number;
  anchorY: number;
  hexHalfW: number;
  viewportW: number;
  viewportH: number;
  /**
   * Extra label/value rows rendered above the alert counts. Map View passes the camera's
   * surveyed install detail (height, facing, tilt, field of view, placement note) so an
   * operator can read WHERE and HOW a camera is mounted without leaving the plan. The
   * hexagon board has no such data and omits it.
   */
  details?: { label: string; value: string }[];
  /** Free-text note shown under `details`. */
  note?: string | null;
  /**
   * Lazy resolver for an alert whose snapshot is not a directly-loadable URL — the cloud
   * blob-fetches the node's saved image through the tunnel. Without it a cloud board's
   * hover card has no picture to show at all.
   */
  loadPreview?: (alert: HexAlert) => Promise<string | null>;
}

export default function HexAlertPopover({
  alert,
  name,
  live,
  recording,
  counter,
  anchorX,
  anchorY,
  hexHalfW,
  viewportW,
  viewportH,
  details,
  note,
  loadPreview,
}: HexAlertPopoverProps) {
  const directUrl = alert?.snapshotUrl || null;
  const previewReady = alert?.previewReady || 0;
  const canResolve = !!(alert && alert.id && loadPreview && !directUrl);

  /**
   * The picture is READ FROM THE CACHE AT RENDER, not held in state, and the effect below only
   * nudges a re-render when a fetch lands. State would be a URL belonging to whichever alert
   * was hovered when it was set — and this component is reused as the pointer moves from one
   * hexagon to the next, so for one frame the new camera's name would sit over the old
   * camera's snapshot. Deriving it means the picture can only ever be this alert's.
   */
  const [, bumpResolved] = useState(0);
  /** Which URL failed to decode — by URL, so a new alert's picture is not pre-judged. */
  const [erroredUrl, setErroredUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!alert || !loadPreview || directUrl) return;
    let cancelled = false;
    // Nothing is revoked on unmount: hexPreviewCache owns these object URLs precisely so a
    // hover ending does not destroy the image the next hover wants.
    resolvePreview(alert, loadPreview).then((url) => {
      if (!cancelled && url) bumpResolved((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert?.id, alert?.cameraId, directUrl, loadPreview, previewReady]);

  const candidate = directUrl || (alert ? cachedPreview(alert) : null);
  const img = candidate && candidate !== erroredUrl ? candidate : null;
  const imgFailed = !!candidate && candidate === erroredUrl;
  /** Reserve the picture's box while it resolves, so the card cannot change height on hover. */
  const showMedia = !!img || canResolve || imgFailed;
  // Flat-top hex aspect: HEX_H / HEX_W = 128 / 144.
  const hexHalfH = hexHalfW * (128 / 144);
  // Rough height only to decide whether there's room above; the card is anchored to the
  // rule by a translate so exact height doesn't matter.
  const detailRows = details?.length ?? 0;
  // Deliberately generous: it only decides whether there is room ABOVE the hex, and
  // over-estimating flips the card below (always correct, occasionally unnecessary) while
  // under-estimating lets it run off the top of the viewport.
  const estH =
    (showMedia ? 218 : 132) +              // snapshot + its overlaid name, or the text-only alert
    (alert?.description ? 28 : 0) +
    detailRows * 15 +
    (note ? 26 : 0);

  const color = alert ? SEV_COLOR[alert.severity] || SEV_COLOR.normal : NEUTRAL_COLOR;

  // Callout direction: up-right by default (ss4), mirrored/flipped to stay in view.
  const fitsRight = anchorX + hexHalfW * 0.55 + DIAG + CARD_W <= viewportW - MARGIN;
  const dirX = fitsRight ? 1 : -1;
  const roomAbove = anchorY - hexHalfH * 0.55 - DIAG - estH >= MARGIN;
  const roomBelow = anchorY + hexHalfH * 0.55 + DIAG + estH <= viewportH - MARGIN;
  const placeBelow = !roomAbove && roomBelow;
  const dirY = placeBelow ? 1 : -1;

  // Leader: dot on the hex's edge → 45° diagonal → elbow, then a horizontal rule the
  // card sits on (the rule doubles as the card's coloured underline).
  const startX = anchorX + dirX * hexHalfW * 0.55;
  const startY = anchorY + dirY * hexHalfH * 0.55;
  const elbowX = startX + dirX * DIAG;
  const elbowY = startY + dirY * DIAG;
  const ruleEndX = elbowX + dirX * CARD_W;

  const cardLeft = dirX > 0 ? elbowX : elbowX - CARD_W;
  const cardTop = placeBelow ? elbowY + 4 : elbowY - 4;
  const cardTransform = placeBelow ? undefined : 'translateY(-100%)';

  const total = counter?.total ?? 0;

  return (
    <>
      <svg className="pointer-events-none absolute left-0 top-0 z-30 h-full w-full overflow-visible">
        <circle cx={startX} cy={startY} r={2.5} fill={color} />
        <line x1={startX} y1={startY} x2={elbowX} y2={elbowY} stroke={color} strokeWidth={1.5} />
        <line x1={elbowX} y1={elbowY} x2={ruleEndX} y2={elbowY} stroke={color} strokeWidth={1.5} />
      </svg>

      <div
        className="pointer-events-none absolute z-30"
        style={{ left: cardLeft, top: cardTop, width: CARD_W, transform: cardTransform }}
      >
        <div className="relative overflow-hidden rounded-md border border-border bg-popover shadow-modal">
          {/* Uppercase callout title in the severity tint (ss4-style label) — the CAMERA,
              which is the one thing the operator already knows they are pointing at, so it
              stays a small label rather than the card's headline. */}
          <div className="flex items-center justify-between gap-2 px-2.5 pb-1.5 pt-2">
            <span
              className="truncate text-[11px] font-bold uppercase tracking-wider"
              style={{ color }}
            >
              {name}
            </span>
            {alert && (
              <span className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${SEV_CHIP[alert.severity] || SEV_CHIP.normal}`}>
                {alert.severity}
              </span>
            )}
          </div>

          {/* ── The alert: what actually happened ─────────────────────────────────── */}
          {alert && (
            <div className="pb-0.5">
              {showMedia ? (
                <div className="relative h-32 w-full bg-muted">
                  {img ? (
                    <img
                      src={img}
                      alt="snapshot"
                      className="h-32 w-full object-cover"
                      onError={() => setErroredUrl(img)}
                    />
                  ) : (
                    /* The box is held whether or not the picture arrives — see the header.
                       A pulsing icon says "coming", and a still one says "there is none",
                       which is a real outcome: the node broadcasts an alert before its
                       storage lane has written the image. */
                    <div className="flex h-32 w-full items-center justify-center">
                      <PhotoIcon
                        className={`h-7 w-7 text-muted-foreground/50 ${imgFailed ? '' : 'animate-pulse'}`}
                        aria-hidden
                      />
                    </div>
                  )}
                  {/* Caption on its own picture, on SOLID chips rather than a gradient.
                      A gradient scrim was the first attempt and it was not enough: these
                      frames are CCTV stills — a bright doorway, a sunlit forecourt, a
                      headlight — and coloured text over one of those washed out completely.
                      Chips guarantee their own contrast whatever the pixel behind them, and
                      they are what AlertTileStack already uses to put readable text over a
                      snapshot. They also leave most of the picture uncovered, which a
                      full-width bar would not. */}
                  <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-1.5 px-1.5 pb-1.5">
                    <span
                      className={`min-w-0 truncate rounded bg-black/75 px-1.5 py-0.5 text-[11px] font-semibold leading-tight ${
                        SEV_NAME_ON_IMAGE[alert.severity] || SEV_NAME_ON_IMAGE.normal
                      }`}
                      title={alert.alertName || alert.eventType || 'Alert'}
                    >
                      {alert.alertName || alert.eventType || 'Alert'}
                    </span>
                    <span className="shrink-0 rounded bg-black/75 px-1.5 py-0.5 font-mono text-[10px] tabular-nums text-white/85">
                      {fmtTime(alert.timestamp)}
                    </span>
                  </div>
                </div>
              ) : (
                /* No snapshot — an alert's image lands after its broadcast, and the cloud
                   proxies node images, so "not yet" is a normal state. The name keeps the
                   same place in the reading order; it just has nothing to sit on. */
                <div className="flex items-baseline justify-between gap-2 px-2.5">
                  <span
                    className={`truncate text-[11px] font-semibold ${
                      SEV_NAME[alert.severity] || SEV_NAME.normal
                    }`}
                  >
                    {alert.alertName || alert.eventType || 'Alert'}
                  </span>
                  <span className="shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground">
                    {fmtTime(alert.timestamp)}
                  </span>
                </div>
              )}

              {alert.description && (
                <p className="line-clamp-2 px-2.5 pt-1.5 text-[10px] leading-snug text-muted-foreground">
                  {alert.description}
                </p>
              )}
            </div>
          )}

          {/* ── The camera: state and history, behind the alert ───────────────────── */}
          <div className="space-y-2 p-2.5">
            {/* ONE row: what the camera is doing (left) and what it is holding (right).
                They were two ruled rows, which on a 248px card spent a third of its height
                saying six words. Together they read as the camera's line — state, then
                tally — and the counts lose their word labels to fit: the dot IS the
                severity everywhere else on this board, and `title` keeps the name for
                anyone who needs it spelled out. */}
            <div className={`flex items-center justify-between gap-2 ${alert ? 'border-t border-border pt-2' : ''}`}>
              <div className="flex items-center gap-3">
                <StatusChip label="Live" on={live} />
                <StatusChip label="Recording" on={recording} />
              </div>
              {total > 0 ? (
                <div className="flex shrink-0 items-center gap-2">
                  {SEV_COUNTS.map((sv) => (
                    <span
                      key={sv.key}
                      title={`${sv.label}: ${counter?.[sv.key] ?? 0}`}
                      className="flex items-center gap-1 text-[10px]"
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${sv.dot}`} />
                      <span className="font-semibold tabular-nums text-popover-foreground">
                        {counter?.[sv.key] ?? 0}
                      </span>
                    </span>
                  ))}
                </div>
              ) : (
                <span className="shrink-0 text-[10px] text-muted-foreground">No alerts</span>
              )}
            </div>

            {/* Surveyed install detail (Map View only) — where and how this camera sits. */}
            {(detailRows > 0 || note) && (
              <div className="space-y-0.5 border-t border-border pt-1.5">
                {details?.map((d) => (
                  <div key={d.label} className="flex items-baseline justify-between gap-2">
                    <span className="text-[10px] text-muted-foreground">{d.label}</span>
                    <span className="truncate text-[10px] font-medium tabular-nums text-popover-foreground">
                      {d.value}
                    </span>
                  </div>
                ))}
                {note && <p className="line-clamp-2 pt-0.5 text-[10px] italic text-muted-foreground">{note}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
